import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import apiService from '../services/apiService';
import { useOrganization } from '../context/OrganizationContext';
import { PricingCheckIcon } from './PricingPlanCardLanding';

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshSubscription, refreshOrganizations } = useOrganization();
  const [countdown, setCountdown] = useState(5);
  const [syncing, setSyncing] = useState(true);
  const [planName, setPlanName] = useState('');
  useEffect(() => {
    const sessionId = searchParams.get('session_id');

    console.log('[SubscriptionSuccess] URL params:', window.location.href);
    console.log('[SubscriptionSuccess] Session ID:', sessionId);

    const syncSubscription = async () => {
      if (!sessionId) {
        console.error('[SubscriptionSuccess] No session ID found in URL');
        toast.error('No session ID found - subscription will be activated via webhook');
        setSyncing(false);
        return;
      }

      console.log('[SubscriptionSuccess] Syncing subscription with session ID:', sessionId);

      try {
        const response = await apiService.syncSubscription(sessionId);
        console.log('[SubscriptionSuccess] Sync response:', response);

        if (response.success) {
          setPlanName(response.subscription.plan);
          toast.success(`Subscription activated: ${response.subscription.plan}!`, {
            id: 'subscription-activated',
          });

          console.log('[SubscriptionSuccess] Refreshing organization context...');
          await refreshSubscription();
          await refreshOrganizations();
          console.log('[SubscriptionSuccess] Organization context refreshed!');
        }
      } catch (error) {
        console.error('[SubscriptionSuccess] Error syncing subscription:', error);
        toast.error('Subscription will be activated shortly');
      } finally {
        setSyncing(false);
      }
    };

    syncSubscription();
    // Sync when Stripe return URL params change; omitting context callbacks avoids duplicate sync on parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Countdown timer
  useEffect(() => {
    if (syncing) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/dashboard');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, syncing]);

  const handleContinue = () => {
    navigate('/dashboard');
  };

  const ctaClass =
    'font-secondary inline-flex w-full min-h-[48px] items-center justify-center whitespace-nowrap rounded-sm bg-primary px-6 py-3 text-[14px] font-semibold leading-tight text-black transition-colors hover:bg-primary-hover';

  return (
    <div className="flex min-h-screen items-center justify-center bg-black px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="w-full max-w-lg rounded-sm border border-white/10 bg-landing-section p-8 sm:p-10"
      >
        {/* Success icon — landing accent */}
        <div className="mb-8 flex justify-center">
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.05, type: 'spring', stiffness: 260, damping: 22 }}
            className="flex h-16 w-16 items-center justify-center rounded-sm bg-primary/15"
            aria-hidden
          >
            <svg
              className="h-9 w-9 text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </motion.div>
        </div>

        <h1 className="font-title text-center text-2xl font-bold tracking-tight text-white sm:text-[28px]">
          Payment successful
        </h1>

        {syncing ? (
          <div className="font-secondary mb-10 mt-6 flex items-center justify-center gap-2 text-[15px] text-gray-300">
            <svg
              className="h-5 w-5 animate-spin text-primary"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="text-center">Activating subscription…</p>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <p className="font-secondary text-center text-[15px] leading-relaxed text-gray-300">
              {planName ? (
                <>
                  <span className="font-semibold text-primary">{planName}</span> subscription activated.
                  You now have access to all the features of your selected plan.
                </>
              ) : (
                <>Your subscription is activated. You now have access to all the features of your selected plan.</>
              )}
            </p>
            <p className="font-secondary text-center text-sm text-gray-400">
              Redirecting to dashboard in{' '}
              <span className="font-semibold text-primary tabular-nums">{countdown}</span> seconds…
            </p>
          </div>
        )}

        <div className="my-8 h-px w-full bg-white/10" />

        <ul className="font-secondary mb-10 flex flex-col gap-5 text-sm leading-[1.4] text-white">
          <li className="flex items-start gap-2.5">
            <PricingCheckIcon />
            <span>Full access to all supported blockchain networks</span>
          </li>
          <li className="flex items-start gap-2.5">
            <PricingCheckIcon />
            <span>Increased rate limits based on your plan</span>
          </li>
          <li className="flex items-start gap-2.5">
            <PricingCheckIcon />
            <span>Priority support and faster response times</span>
          </li>
        </ul>

        <motion.button
          type="button"
          onClick={handleContinue}
          className={ctaClass}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Continue to Dashboard
        </motion.button>

        <p className="font-secondary mt-6 text-center text-sm text-gray-400">
          A confirmation email has been sent to your registered email address.
        </p>
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccess;
